import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  FormLabel,
  Grid,
  Tabs,
  Tab,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import _ from "lodash";

import { getShipmentDetails, getShipmentPackages } from "api/shipment";

import { StatusLabel } from "components/Custom";
import { ExternalLink } from "components/Custom/Links";
import PickStatus from "components/Custom/PickStatus";
import DefaultTable from "components/Table";
import Widget from "components/Widget";

import { useLayoutState } from "context/LayoutContext";

import DetailTimeline from "pages/shipments/ShipmentDetailTimeline";

import { convertDataToTableView } from "utils/convert";
import { exportToExcel } from "utils/exportToExcel";
import {
  ACUMATICA_BASE_URL,
  initialAPIListValue,
  INVOICE_URL,
  ORDER_URL,
  SHIPMENT_URL,
} from "utils/constants";
import { timezoneFormat } from "utils/date";

import useStyles from "pages/ltl/styles";

const packageDetailHeaders = [
  {
    id: "sku",
    render: (r) => r.product.sku,
  },
  {
    id: "quantity",
  },
];

// Read-only LTL shipment preview for OBT/seller users. It is identical to the
// internal LTL shipment preview, only without any actions (no edit/delete/
// complete/reopen packages, note editing or label downloads).
export default function LTLShipmentPreview({ shipment }) {
  const classes = useStyles();
  const [t] = useTranslation();
  const { isMobile } = useLayoutState();
  const [activeTab, setActiveTab] = useState(0);
  const [details, setDetails] = useState(initialAPIListValue);
  const [packages, setPackages] = useState(initialAPIListValue);
  const [selectedPackage, setSelectedPackage] = useState({});
  const [timelineRow, setTimelineRow] = useState({});
  const [itemCount, setItemCount] = useState(0);
  const [totalPicked, setTotalPicked] = useState(0);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [orderBy, setOrderBy] = useState("product.sku");
  const [reSort, setReSort] = useState(true);
  const [exportDisabled, setExportDisabled] = useState(false);

  const popoverContainer = useRef(null);

  const previewHeaders = [
    {
      id: "product.sku",
      label: "SKU",
      render: (item) => item.product.sku,
      sortable: true,
    },
    {
      id: "quantity",
      label: "qty",
      render: (item) => (
        <PickStatus
          total_qty={item.quantity}
          picked_qty={item.transactions
            .filter((t) => t.action_type === "Picked")
            .reduce((total, t) => total + t.quantity, 0)}
        />
      ),
      sortable: true,
    },
    {
      id: "location.code",
      label: "location",
      render: (item) => item.location.code,
      sortable: true,
    },
    {
      id: "status",
      render: (item) => <StatusLabel status={item.status} />,
      sortable: true,
    },
    {
      id: "note",
      render: (item) =>
        item.note ? (
          <Alert severity="warning" variant="filled">
            {item.note}
          </Alert>
        ) : (
          "-"
        ),
      sortable: true,
    },
  ];

  const exportHeaders = [
    {
      id: "product.sku",
      label: "SKU",
      render: (item) => item.product.sku,
    },
    {
      id: "quantity",
      label: "qty",
      render: (item) => {
        var itemQty = item.transactions
          .filter((t) => t.action_type === "Picked")
          .reduce((total, t) => total + t.quantity, 0);
        return itemQty === item.quantity
          ? item.quantity
          : `${itemQty} / ${item.quantity}`;
      },
    },
    { id: "status" },
    { id: "note" },
  ];

  const packageHeaders = [
    {
      id: "name",
      render: (r) =>
        `${r.name} (${r.details.reduce(
          (total, detail) => total + detail.quantity,
          0,
        )})`,
    },
    {
      id: "note",
      render: (r) => r.note || "-",
    },
    {
      id: "status",
      render: (r) => <StatusLabel status={r.status} />,
    },
  ];

  useEffect(() => {
    if (details.isFetching) {
      getShipmentDetails({
        params: {
          shipment_id: shipment.id,
          limit: 9999,
        },
        responseSetter: (res) => {
          const { results } = res;
          setDetails({
            items: _.orderBy(results, [orderBy], [orderDirection]),
            isFetching: false,
          });
          if (!isMobile && results.length > 0) {
            setTimelineRow(results[0]);
            let totalPicked = 0;
            let totalQuantity = 0;
            results.forEach((detail) => {
              totalQuantity += detail.quantity;
              totalPicked += detail.transactions
                .filter((t) => t.action_type === "Picked")
                .reduce((total, t) => total + t.quantity, 0);
            });
            setTotalPicked(totalPicked);
            setItemCount(totalQuantity);
          }
        },
      });
    }
  }, [shipment.id, details.isFetching, isMobile, orderBy, orderDirection]);

  useEffect(() => {
    if (reSort) {
      setDetails((prev) => ({
        ...prev,
        items: _.orderBy(prev.items, [orderBy], [orderDirection]),
      }));
      setReSort(false);
    }
  }, [reSort, orderBy, orderDirection]);

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setReSort(true);
  };

  const handleChangeTab = (event, key) => {
    setActiveTab(key);
    if (key === 0) {
      setDetails((prev) => ({
        ...prev,
        isFetching: true,
      }));
    } else {
      setPackages(initialAPIListValue);
      getShipmentPackages({
        params: {
          shipment_number: shipment.shipment_number,
        },
        setFunc: setPackages,
        paginated: true,
      });
    }
  };

  const handleSelectPackage = (row) => {
    setSelectedPackage(row);
  };

  const exportData = (data) => {
    exportToExcel(
      [],
      `LTL-Shipment-${shipment.shipment_number}`,
      true,
      convertDataToTableView(data, exportHeaders),
      setExportDisabled,
    );
  };

  const handleExportData = () => {
    getShipmentDetails({
      params: {
        shipment_id: shipment.id,
        limit: 9999,
      },
      setFunc: ({ items: { results } }) =>
        exportData(_.orderBy(results, [orderBy], [orderDirection])),
    });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <Widget title={t("shipment")} style={{ minHeight: "12rem" }}>
            <Grid container>
              <Grid item lg={3} xs={12}>
                <div>
                  <FormLabel component="h6" className={classes.label}>
                    {t("shipment_number")}
                  </FormLabel>
                  {shipment.shipment_number ? (
                    <ExternalLink
                      className={classes.link}
                      to={`${ACUMATICA_BASE_URL}${SHIPMENT_URL}${shipment.shipment_number}`}
                      children={shipment.shipment_number}
                    />
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </Grid>
              <Grid item lg={3} xs={12}>
                <div>
                  <FormLabel component="h6" className={classes.label}>
                    {t("order_number")}
                  </FormLabel>
                  {shipment.order && shipment.order.number ? (
                    <ExternalLink
                      className={classes.link}
                      to={`${ACUMATICA_BASE_URL}${ORDER_URL}${shipment.order.number}`}
                      children={shipment.order.number}
                    />
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </Grid>
              <Grid item lg={3} xs={12}>
                <div>
                  <FormLabel component="h6" className={classes.label}>
                    {t("invoice_number")}
                  </FormLabel>
                  {shipment.invoice_number ? (
                    <ExternalLink
                      className={classes.link}
                      to={`${ACUMATICA_BASE_URL}${INVOICE_URL}${shipment.invoice_number}`}
                      children={shipment.invoice_number}
                    />
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </Grid>
              <Grid item lg={3} xs={12}>
                <div>
                  <FormLabel component="h6" className={classes.label}>
                    {t("customer")}
                  </FormLabel>
                  <span>
                    {shipment.order ? shipment.order.customer_id : "-"}
                  </span>
                </div>
              </Grid>
              <Grid item lg={3} xs={12}>
                <FormLabel component="h6" className={classes.label}>
                  {t("status")}
                </FormLabel>
                <StatusLabel status={shipment.status} />
              </Grid>
              <Grid item lg={3} xs={12}>
                <FormLabel component="h6" className={classes.label}>
                  {t("invoiced_at")}
                </FormLabel>
                <span>
                  {shipment.invoiced_at
                    ? timezoneFormat(shipment.invoiced_at)
                    : "-"}
                </span>
              </Grid>
              <Grid item lg={3} xs={12}>
                <FormLabel component="h6" className={classes.label}>
                  {t("created_at")}
                </FormLabel>
                <span>{timezoneFormat(shipment.created_date)}</span>
              </Grid>
              <Grid item lg={3} xs={12}>
                <FormLabel component="h6" className={classes.label}>
                  {t("ship_via")}
                </FormLabel>
                <span>{shipment.ship_via || "-"}</span>
              </Grid>
              <Grid item lg={3} xs={12}>
                <FormLabel component="h6" className={classes.label}>
                  {t("total_weight")}
                </FormLabel>
                <span>{shipment.total_weight || "-"}</span>
              </Grid>
              <Grid item lg={3} xs={12}>
                <FormLabel component="h6" className={classes.label}>
                  {t("total_sqryards")}
                </FormLabel>
                <span>{shipment.total_sqryards || "-"}</span>
              </Grid>
            </Grid>
          </Widget>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Widget>
            <Tabs value={activeTab} onChange={handleChangeTab}>
              <Tab label="Items" id={0} />
              <Tab label="Packages" id={1} />
            </Tabs>
            <TabPanel value={activeTab} index={0}>
              <Grid container>
                <Grid item lg={8} xs={12}>
                  <Widget>
                    {details.isFetching ? (
                      <CircularProgress />
                    ) : !details.items.length ? (
                      t("no_results_found")
                    ) : (
                      <Grid container>
                        <Grid item lg={12}>
                          <Typography>{`(${totalPicked}/${itemCount})`}</Typography>
                        </Grid>
                        <Grid item lg={12}>
                          <DefaultTable
                            data={details}
                            headers={previewHeaders}
                            selected={[timelineRow]}
                            rowOnClick={(r) => setTimelineRow(r)}
                            sortActions={{ onSort, orderBy, orderDirection }}
                            handleExportData={handleExportData}
                            exportDisabled={exportDisabled}
                            setExportDisabled={setExportDisabled}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Widget>
                </Grid>
                <Grid item lg={4} xs={12} ref={popoverContainer}>
                  {timelineRow && timelineRow.id ? (
                    <DetailTimeline
                      detailId={timelineRow.id}
                      anchorRef={popoverContainer}
                      onClose={() => setTimelineRow({})}
                      isMobile={isMobile}
                    />
                  ) : null}
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              {packages.isFetching ? (
                <CircularProgress />
              ) : !packages.items.length ? (
                <Alert severity="info"> Packages not found! </Alert>
              ) : (
                <Widget>
                  <Grid container spacing={4}>
                    <Grid item lg={7} xs={12}>
                      <DefaultTable
                        headers={packageHeaders}
                        data={packages}
                        rowOnClick={handleSelectPackage}
                        highlightFunc={(r) => r.id === selectedPackage.id}
                        highlightColor="#EAEDF0"
                      />
                    </Grid>
                    <Grid item lg={5} xs={12}>
                      {!Object.keys(selectedPackage).length ? (
                        <Alert severity="info"> Please select a package </Alert>
                      ) : (
                        <DefaultTable
                          headers={packageDetailHeaders}
                          data={{
                            items: selectedPackage.details,
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Widget>
              )}
            </TabPanel>
          </Widget>
        </Grid>
      </Grid>
    </>
  );
}

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}
